import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useDashboardStore } from 'store/dashboard.store';

import { useMutationSetDashboardById } from 'api/mutations/setDashboardById.mutation';
import { useQueryGetDashboards } from 'api/queries/getDashboards.query';

import { CustomizedRoundIcon } from 'components/common/CustomizedRoundIcon/CustomizedRoundIcon';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';

import { ModalManager } from 'components/Dashboards/Project/Components/CustomDashboard/ModalManager/ModalManager';
import {
    deleteWidgetFromDashboard,
    getCurrentDashboardId,
} from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import {
    IDashboard,
    IModalConfig,
    IRemoveWidget,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedTooltip } from 'components/common';

/**
 * Remove the Widget by ID .Part of widgetTopHeader
 * @param {string}  widgetId - unique ID created when adding a new widget to a specific pinboard.
 *
 * @returns  Remove Widget Modal
 * */

export const RemoveWidget = ({ widgetId }: IRemoveWidget) => {
    const { dashboardId } = useParams<any>();
    const { dashboard: selectedDashboardId } = useDashboardStore((store) => store.selectedDashboard);
    const [modalConfirmManagerOpen, setModalConfirmManagerOpen] = useState(false);
    const { handleSnackBar } = useCustomSnackBar();
    const { data, refetch } = useQueryGetDashboards();
    const dashboards: IDashboard[] = data?.dashboardDetails || [];
    const { mutate: mutateSetDashboardById } = useMutationSetDashboardById();
    const dashboard: IDashboard | undefined = getCurrentDashboardId({ dashboards, dashboardId, selectedDashboardId });

    const { ability } = useUserHook();

    const handleRemoveWidgetFromDashboard = () => {
        const updatedDashboard = deleteWidgetFromDashboard({
            dashboard,
            widgetId,
        });
        dashboard &&
            mutateSetDashboardById(
                { dashboard: updatedDashboard },
                {
                    onSuccess: () => handleSnackBar('Successfully removed!', 'success'),
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                    onSettled: () => refetch(),
                },
            );
    };

    const modalsConfig: { [key: string]: IModalConfig } = {
        RemoveWidgetResponse: {
            onSaveFunction: () => handleRemoveWidgetFromDashboard(),
            onCloseFunction: () => setModalConfirmManagerOpen(false),
            dashboards,
            value: { dashboard },
        },
    };
    if (!ability.can('update', 'PinboardsEdit')) {
        return null;
    }
    return (
        <>
            <CustomizedTooltip
                tooltipPosition={TooltipPosition.Top}
                tooltipContent={`Remove from ${dashboard?.name}`}
                triggerElement={
                    <CustomizedRoundIcon size={'20px'} onClick={() => setModalConfirmManagerOpen(true)}>
                        <Icon name={'filter-x-icon'} size={'20px'} />
                    </CustomizedRoundIcon>
                }
            />
            {modalConfirmManagerOpen && <ModalManager modalsConfig={modalsConfig} modalType={'RemoveWidgetResponse'} />}
        </>
    );
};
